import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Bell, BellOff, BellRing, CheckCheck, ClipboardCheck, Share, Wrench } from 'lucide-react';
import MobileListScreen from './MobileListScreen';
import {
    fetchNotifications,
    markNotificationRead,
    markAllNotificationsRead,
} from '../../store/slices/notificationSlice';
import { PushState, enablePush, getPushState } from '../../pwa/pushSubscription';
import { isIos, isStandalone } from '../../pwa/useInstallPrompt';

/**
 * What happened, for one mechanic.
 *
 * Two kinds of thing land here: a job was assigned to you, and a customer answered an
 * approval. Both name the REGO, because that is the identifier someone standing next
 * to a car recognises — a job number means nothing there.
 *
 * ## The list is the feature; push is a bonus
 *
 * Everything on this screen works with push permission denied, on an iPhone that never
 * installed the app, and with no VAPID key configured on the server. The notification
 * rows exist regardless; push only decides whether the phone buzzes before someone
 * opens the app. That is why the enable prompt is a dismissible card and not a gate.
 *
 * ## Why the prompt says which thing is wrong
 *
 * "Notifications are off" is useless — the fix differs completely depending on whether
 * the browser cannot do push, the user tapped Block, or the server has no key pair.
 * getPushState() reports those separately and each gets its own sentence.
 *
 * ⚠️ UNSUPPORTED splits again, and it matters. On an iPhone it means "not installed
 * yet", which the person can fix in three taps and which workshops require of staff.
 * On anything else it means the browser genuinely cannot do push, and telling that
 * person to use the iOS Share sheet is instructions for a device they are not holding.
 *
 * The install message here is deliberately NOT the InstallCard from PwaBanners. That
 * card is a dismissible convenience — "add it for full-screen access" — and it stays
 * dismissed for thirty days. Here installing is the prerequisite for the feature the
 * person has just opened, so it is stated as a requirement and does not hide.
 */

const TYPE_ICON = {
    JOB_ASSIGNED: Wrench,
    CUSTOMER_APPROVAL: ClipboardCheck,
};

/** Relative time, to the resolution a workshop actually cares about. */
export const relativeTime = (iso, now = Date.now()) => {
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return '';
    const seconds = Math.max(0, Math.floor((now - then) / 1000));
    if (seconds < 60) return 'just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return days === 1 ? 'yesterday' : `${days}d ago`;
};

const NotificationRow = ({ notification, onOpen }) => {
    const Icon = TYPE_ICON[notification.type] ?? Bell;
    const unread = !notification.readAt;

    return (
        <li className={`border-b border-gray-100 ${unread ? 'bg-blue-50/60' : 'bg-white'}`}>
            <button
                type="button"
                onClick={() => onOpen(notification)}
                className="flex w-full items-start gap-3 px-4 py-3 text-left active:bg-gray-100"
            >
                <span className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full
                                  ${unread ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-400'}`}>
                    <Icon size={15} />
                </span>
                <span className="min-w-0 flex-1">
                    <span className={`block text-sm ${unread ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                        {notification.title}
                    </span>
                    <span className="mt-0.5 block text-xs text-gray-500">{notification.body}</span>
                    <span className="mt-1 block text-[11px] text-gray-400">
                        {relativeTime(notification.createdAt)}
                    </span>
                </span>
                {unread && <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-blue-600" aria-label="Unread" />}
            </button>
        </li>
    );
};

/** The "turn notifications on" card, and the honest explanation when it cannot be. */
const PushPrompt = ({ state, onEnable, busy }) => {
    if (state === PushState.ENABLED || state === null) return null;

    // An iPhone that has not been added to the home screen yet — the one "unsupported"
    // that the person holding the phone can actually fix.
    const needsInstall = isIos() && !isStandalone();

    const copy = {
        [PushState.PROMPT]: {
            icon: BellRing,
            title: 'Get notified on this phone',
            detail: 'Be told when a job is assigned to you or a customer approves work, even when the app is closed.',
            action: 'Turn on',
        },
        [PushState.DENIED]: {
            icon: BellOff,
            title: 'Notifications are blocked',
            // Nothing in the app can undo this — only the browser's own settings can,
            // so saying "turn on" here would be a button that cannot work.
            detail: 'You blocked notifications for this site. Allow them in your browser settings to turn them back on.',
            action: null,
        },
        [PushState.UNSUPPORTED]: needsInstall
            ? {
                icon: Share,
                title: 'Install Wadakaroo to get notifications',
                // ⚠️ The iPhone case: Safari exposes push ONLY to an installed PWA.
                // Three taps, and then it works exactly as it does on Android.
                detail: 'On iPhone, notifications only work once the app is on your home screen. Tap Share, then Add to Home Screen, and open Wadakaroo from there.',
                action: null,
            }
            : {
                icon: BellOff,
                title: 'This browser cannot show notifications',
                // Not the install message: they are not on an iPhone, so the Share
                // sheet does not exist and would be instructions for another device.
                detail: 'Your notifications are still listed here. Open Wadakaroo in Chrome or Safari to be notified when the app is closed.',
                action: null,
            },
        [PushState.NO_WORKER]: {
            icon: BellOff,
            // The background worker is what receives a push; without it there is
            // nothing to deliver to. Normal in `npm run dev`, where the worker is
            // deliberately not registered — and a real fault anywhere else.
            title: 'Notifications are not available here',
            detail: 'The app’s background worker is not running, so nothing can be delivered to this browser. Reload the app, or use the installed version.',
            action: null,
        },
        [PushState.NOT_CONFIGURED]: {
            icon: BellOff,
            title: 'Push notifications are not set up',
            detail: 'Your notifications are still listed here. Ask your administrator to finish setting up push.',
            action: null,
        },
    }[state];

    if (!copy) return null;
    const Icon = copy.icon;

    return (
        <div className="mx-4 my-3 rounded-xl border border-blue-200 bg-blue-50 p-3">
            <div className="flex items-start gap-2.5">
                <Icon size={18} className="mt-0.5 shrink-0 text-blue-600" />
                <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-blue-900">{copy.title}</p>
                    <p className="mt-0.5 text-xs text-blue-800">{copy.detail}</p>
                    {copy.action && (
                        <button
                            type="button"
                            onClick={onEnable}
                            disabled={busy}
                            className="mt-2 min-h-[40px] rounded-xl bg-blue-600 px-4 text-sm font-semibold
                                       text-white active:bg-blue-700 disabled:opacity-50"
                        >
                            {busy ? 'Turning on…' : copy.action}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

const MobileNotificationsPage = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { notifications, unreadCount, loading, error } = useSelector((s) => s.notifications);

    const [pushState, setPushState] = useState(null);
    const [enabling, setEnabling] = useState(false);

    const load = useCallback(() => dispatch(fetchNotifications()), [dispatch]);

    useEffect(() => { load(); }, [load]);

    // Read-only, prompts nothing — safe on mount. Actually ASKING for permission is
    // only ever done from the button; browsers ignore a request made on page load.
    useEffect(() => {
        let cancelled = false;
        getPushState()
            .then((state) => { if (!cancelled) setPushState(state); })
            .catch(() => { if (!cancelled) setPushState(PushState.UNSUPPORTED); });
        return () => { cancelled = true; };
    }, []);

    const handleEnable = async () => {
        setEnabling(true);
        try {
            setPushState(await enablePush());
        } catch {
            setPushState(PushState.UNSUPPORTED);
        } finally {
            setEnabling(false);
        }
    };

    const handleOpen = (notification) => {
        if (!notification.readAt) dispatch(markNotificationRead(notification.uuid));
        // A notification whose job has been deleted still reads correctly; it just has
        // nowhere to go, so it stays put rather than navigating to a broken page.
        if (notification.jobUuid) navigate(`/jobs?job=${notification.jobUuid}`);
    };

    return (
        <MobileListScreen
            title="Notifications"
            action={unreadCount > 0 ? (
                <button
                    type="button"
                    onClick={() => dispatch(markAllNotificationsRead())}
                    className="flex min-h-[40px] items-center gap-1.5 rounded-xl bg-gray-100 px-3
                               text-sm font-medium text-gray-700 active:bg-gray-200"
                >
                    <CheckCheck size={15} /> Mark all read
                </button>
            ) : null}
            loading={loading}
            error={error}
            onRefresh={load}
            items={notifications}
            banner={<PushPrompt state={pushState} onEnable={handleEnable} busy={enabling} />}
            renderItem={(notification) => (
                <NotificationRow key={notification.uuid} notification={notification} onOpen={handleOpen} />
            )}
            // ⚠️ An OBJECT, not a string. ListEmpty reads .title/.detail off it, so a
            // string renders an empty box — which is exactly what a screen with no
            // notifications yet showed: nothing at all.
            empty={{
                icon: Bell,
                title: 'Nothing yet',
                detail: "You'll be told when a job is assigned to you or a customer replies.",
            }}
        />
    );
};

export default MobileNotificationsPage;
